import { useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { Sparkles } from 'lucide-react';
import {
  CoachBadRequestError,
  CoachBudgetExceededError,
  CoachInvalidPlanError,
  CoachNotConfiguredError,
  CoachPlanRequestSchema,
  CoachUpstreamError,
  DAY_KEYS,
  GOAL_VALUES,
  useGeneratePlan,
  type CoachPlanRequest,
  type CoachPlanResponse,
  type DayKey,
  type Goal,
  type HoursPerDay,
} from '@/lib/coach';
import { useActiveSeason } from '@/lib/season';
import { useJobs } from '@/lib/jobs';

/**
 * Formulario para generar un plan semanal con el Coach IA.
 *
 * Decisiones:
 * - Date picker: usamos `<input type="date">` apuntando al lunes objetivo.
 *   Es más portable que `type="week"` (que renderiza distinto en cada
 *   navegador y, en jsdom, ni siquiera se controla). El default es el lunes
 *   de la SIGUIENTE semana, calculado en TZ local del navegador para que el
 *   usuario vea una fecha esperable.
 * - Validación: la hacemos client-side con `CoachPlanRequestSchema` antes de
 *   enviar (mismo schema que valida el backend), igual que hace
 *   `ProfileEditForm`. No añadimos `react-hook-form`: lista corta de campos.
 * - Preview default true: el usuario quiere VER antes de comprometer. Si lo
 *   acepta luego, `PlanPreview` reenvía con `preview: false`.
 */
export function NewPlanForm({
  onResult,
}: {
  onResult: (result: CoachPlanResponse, request: CoachPlanRequest) => void;
}): ReactElement {
  const mutation = useGeneratePlan();
  const seasonQuery = useActiveSeason();
  const { startJob } = useJobs();
  const hasSeasonPlan = seasonQuery.data !== null && seasonQuery.data !== undefined;

  const [weekStart, setWeekStart] = useState<string>(() => nextMondayISODate());
  // M2.25 — Si hay plan de temporada activo, modo "auto" por defecto: el
  // backend deriva goal + horas de la fase actual. El usuario puede pasar a
  // manual desactivando el checkbox.
  const [useAutoFromSeason, setUseAutoFromSeason] = useState<boolean>(true);
  const [goal, setGoal] = useState<Goal>('base');
  const [hoursPerDay, setHoursPerDay] = useState<HoursPerDay>({
    mon: 1,
    tue: 1,
    wed: 1,
    thu: 1,
    fri: 1,
    sat: 3,
    sun: 2,
  });
  const [constraints, setConstraints] = useState<string>('');
  // M2.46.B — Preset de énfasis para la semana. Cuando != 'balanced',
  // prepende una instrucción explícita al `constraints` que el LLM lee.
  // Cero cambios backend: lo escribimos como texto estructurado dentro del
  // mismo campo `constraints` (validado: max 1000 chars).
  const [emphasis, setEmphasis] = useState<EmphasisPreset>('balanced');
  // M2.26 — default false: persistir el plan generado en BD aunque el
  // usuario navegue durante los 30-90s de generación. Antes era true y se
  // perdía si te ibas.
  const [preview, setPreview] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const totalHours = useMemo(() => sumHours(hoursPerDay), [hoursPerDay]);
  // El modo auto está activo SI hay plan + el checkbox está marcado.
  const autoActive = hasSeasonPlan && useAutoFromSeason;

  function buildRequest(): CoachPlanRequest {
    // Convertimos `YYYY-MM-DD` (input value) a ISO 8601 UTC del día. El
    // backend re-interpreta según la TZ del profile; basta con que sea un ISO
    // válido y que represente la fecha pretendida.
    const weekStartISO = weekStart
      ? new Date(`${weekStart}T00:00:00Z`).toISOString()
      : '';
    const req: CoachPlanRequest = autoActive
      ? {
          // Modo auto (M2.25): backend deriva goal + horas del plan activo.
          weekStartISO,
          goal: 'auto',
          preview,
        }
      : {
          weekStartISO,
          goal,
          hoursPerDay,
          preview,
        };
    // M2.46.B — Componer constraints = [bloque énfasis] + [texto usuario].
    const userText = constraints.trim();
    const emphasisBlock =
      emphasis === 'balanced' ? '' : emphasisInstruction(emphasis);
    const finalConstraints = [emphasisBlock, userText]
      .filter((s) => s.length > 0)
      .join('\n\n');
    if (finalConstraints) req.constraints = finalConstraints;
    return req;
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (mutation.isPending) return;

    const candidate = buildRequest();
    const parsed = CoachPlanRequestSchema.safeParse(candidate);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? '');
        if (key && !next[key]) next[key] = issue.message;
      }
      setFieldErrors(next);
      return;
    }
    setFieldErrors({});

    // M2.27 — disparamos la mutation Y registramos un job global. El job
    // sobrevive a navegaciones (banner flotante en AppLayout), la mutation
    // local sigue alimentando el spinner del form si el usuario se queda.
    const promise = mutation.mutateAsync(parsed.data);
    // M2.28 — Si el plan se persiste, llevamos al usuario directamente a la
    // semana correcta del calendario `/entrenos?week=YYYY-MM-DD&view=week`,
    // no a la semana actual (que puede estar vacía). WorkoutsPage lee el
    // query param al montar (M2.28).
    const weekISO = parsed.data.weekStartISO.slice(0, 10); // YYYY-MM-DD
    const resultPath =
      parsed.data.preview === false
        ? `/entrenos?view=week&week=${weekISO}`
        : '/coach/nuevo-plan';
    startJob({
      type: 'coach-plan',
      label: `Coach IA · semana del ${shortDate(parsed.data.weekStartISO)}`,
      promise,
      resultPath,
    });
    promise.then(
      (result) => onResult(result, parsed.data),
      () => {
        /* el banner pinta el error; el form también via mutation.error */
      },
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-6" aria-labelledby="coach-form-title">
      <header>
        <h1 id="coach-form-title" className="text-2xl font-semibold tracking-tight text-slate-900">
          Generar plan semanal
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          El coach diseñará tus entrenamientos para la semana elegida. Tarda
          30-90 segundos — puedes navegar mientras se genera, el plan queda
          guardado en BD al terminar y se ve en el calendario.
        </p>
      </header>

      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Semana
        </h2>
        <div className="mt-3 space-y-3">
          <div>
            <label htmlFor="weekStart" className="block text-sm font-medium text-slate-700">
              Lunes de la semana objetivo
            </label>
            <input
              id="weekStart"
              type="date"
              value={weekStart}
              onChange={(e) => setWeekStart(e.target.value)}
              aria-invalid={fieldErrors.weekStartISO !== undefined}
              aria-describedby={fieldErrors.weekStartISO ? 'weekStart-error' : undefined}
              className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600 sm:w-auto"
            />
            {fieldErrors.weekStartISO && (
              <p id="weekStart-error" role="alert" className="mt-1 text-xs text-rose-700">
                {fieldErrors.weekStartISO}
              </p>
            )}
            <p className="mt-1 text-xs text-slate-500">
              Si no es un lunes, el backend lo ajustará al lunes de esa semana.
            </p>
          </div>
        </div>
      </section>

      {/* M2.25 — Banner condicional: si hay plan de temporada activo,
          ofrecemos modo "auto" (backend deriva goal + horas de la fase
          actual). Por defecto activo; el usuario puede pasar a manual. */}
      {hasSeasonPlan && seasonQuery.data && (
        <section
          data-testid="coach-season-banner"
          className="rounded-lg border border-teal-200 bg-teal-50 p-4 shadow-sm"
        >
          <div className="flex items-start gap-3">
            <Sparkles aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-teal-700" />
            <div className="flex-1">
              <h2 className="text-sm font-semibold text-teal-900">
                Plan de temporada activo
              </h2>
              <p className="mt-1 text-xs text-teal-800">
                <strong>{seasonQuery.data.name}</strong> ·{' '}
                evento {seasonQuery.data.eventDateISO}.
                {autoActive ? (
                  <>
                    {' '}
                    El coach derivará el objetivo y la distribución de horas a
                    partir de la fase actual del plan automáticamente.
                  </>
                ) : (
                  <>
                    {' '}
                    Modo manual activo: defines objetivo y horas tú.
                  </>
                )}
              </p>
              <label className="mt-3 inline-flex cursor-pointer items-center gap-2 text-xs text-teal-900">
                <input
                  type="checkbox"
                  checked={useAutoFromSeason}
                  onChange={(e) => setUseAutoFromSeason(e.target.checked)}
                  className="rounded border-teal-400 text-teal-600 focus:ring-teal-600"
                  data-testid="coach-auto-toggle"
                />
                <span>Usar configuración del plan (recomendado)</span>
              </label>
            </div>
          </div>
        </section>
      )}

      {/* Objetivo manual — sólo si no hay plan o el usuario desactivó auto. */}
      {!autoActive && (
        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Objetivo de la semana
          </h2>
          <fieldset className="mt-3">
            <legend className="sr-only">Objetivo</legend>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {GOAL_VALUES.map((g) => (
                <label
                  key={g}
                  className={`flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 text-sm transition ${
                    goal === g
                      ? 'border-teal-600 bg-teal-50 text-teal-900'
                      : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="goal"
                    value={g}
                    checked={goal === g}
                    onChange={() => setGoal(g)}
                    className="text-teal-600 focus:ring-teal-600"
                  />
                  <span>{goalLabel(g)}</span>
                </label>
              ))}
            </div>
          </fieldset>
        </section>
      )}

      {/* Horas por día — sólo en modo manual (M2.25). */}
      {!autoActive && (
        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Horas disponibles por día
            </h2>
            <p className="text-xs text-slate-600">
              Total: <strong className="text-slate-900">{formatHours(totalHours)}</strong>
            </p>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
            {DAY_KEYS.map((day) => (
              <div key={day}>
                <label htmlFor={`hours-${day}`} className="block text-xs font-medium text-slate-700">
                  {dayLabel(day)}
                </label>
                <input
                  id={`hours-${day}`}
                  type="number"
                  min={0}
                  max={24}
                  step={0.5}
                  value={hoursPerDay[day]}
                  onChange={(e) =>
                    setHoursPerDay((prev) => ({
                      ...prev,
                      [day]: clampHours(e.target.value),
                    }))
                  }
                  aria-invalid={fieldErrors.hoursPerDay !== undefined}
                  className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
                />
              </div>
            ))}
          </div>
          {fieldErrors.hoursPerDay && (
            <p role="alert" className="mt-2 text-xs text-rose-700">
              {fieldErrors.hoursPerDay}
            </p>
          )}
        </section>
      )}

      {/* M2.46.B — Énfasis de la semana (preset). El backend recibe esto
          como bloque de texto prepended al campo `constraints`. */}
      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Énfasis de esta semana
        </h2>
        <p className="mt-1 text-xs text-slate-600">
          Indica si quieres priorizar alguna disciplina o cargar más / menos
          que tu plan general. El coach lo respeta dentro de las horas
          disponibles.
        </p>
        <label htmlFor="emphasis" className="sr-only">
          Énfasis
        </label>
        <select
          id="emphasis"
          value={emphasis}
          onChange={(e) => setEmphasis(e.target.value as EmphasisPreset)}
          data-testid="coach-emphasis-select"
          className="mt-3 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600 sm:w-auto"
        >
          {EMPHASIS_PRESETS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
        {emphasis !== 'balanced' && (
          <p className="mt-2 rounded bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <strong>Instrucción al coach:</strong>{' '}
            {emphasisInstruction(emphasis)}
          </p>
        )}
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Restricciones (opcional)
        </h2>
        <label htmlFor="constraints" className="sr-only">
          Restricciones
        </label>
        <textarea
          id="constraints"
          rows={3}
          maxLength={1000}
          value={constraints}
          onChange={(e) => setConstraints(e.target.value)}
          placeholder="Ej: lesión leve en rodilla derecha, evitar series de carrera; piscina cerrada el miércoles."
          aria-invalid={fieldErrors.constraints !== undefined}
          className="mt-3 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
        />
        <p className="mt-1 text-xs text-slate-500">
          {constraints.length} / 1000 caracteres
        </p>
        {fieldErrors.constraints && (
          <p role="alert" className="mt-1 text-xs text-rose-700">
            {fieldErrors.constraints}
          </p>
        )}
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <label className="flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            checked={preview}
            onChange={(e) => setPreview(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-600"
          />
          <span>
            <span className="font-medium text-slate-900">Solo previsualizar</span>
            <span className="block text-xs text-slate-600">
              Revisa el plan antes de guardarlo en tu calendario. Recomendado.
            </span>
          </span>
        </label>
      </section>

      {mutation.isPending && (
        <div
          role="status"
          aria-live="polite"
          aria-busy="true"
          className="flex items-center gap-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          <Spinner />
          <span>
            El coach está pensando…{' '}
            <span className="text-xs text-amber-800">
              (esto puede tardar hasta 30 segundos)
            </span>
          </span>
        </div>
      )}

      {mutation.isError && <ErrorBanner error={mutation.error} />}

      <div className="flex items-center justify-end gap-3">
        <button
          type="submit"
          disabled={mutation.isPending}
          aria-busy={mutation.isPending}
          className="rounded-md bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? (
            <span className="inline-flex items-center gap-2">
              <Spinner /> Generando…
            </span>
          ) : (
            'Generar plan'
          )}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Banner de error
// ---------------------------------------------------------------------------

function ErrorBanner({ error }: { error: Error }): ReactElement {
  const message = mapCoachError(error);
  return (
    <div
      role="alert"
      className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800"
    >
      {message}
    </div>
  );
}

function mapCoachError(err: Error): string {
  if (err instanceof CoachBudgetExceededError) {
    // Mensaje específico M1.10. Incluimos el hint del backend si vino (suele
    // contener la pista accionable) y respaldamos con la copia del contrato si
    // no llega.
    return (
      err.hint ||
      'Has alcanzado tu presupuesto mensual de Anthropic. Súbelo en /ajustes/anthropic o espera al mes siguiente.'
    );
  }
  if (err instanceof CoachNotConfiguredError) {
    // Desde M1.10 la API key de Anthropic vive por profile (no en .env). El
    // hint del backend ya apunta al usuario a `/ajustes/anthropic`; lo
    // antependemos con el aviso para que el alert siga conteniendo la pista
    // de qué hace falta configurar — los tests existentes buscan
    // /anthropic_api_key/i en el mensaje, así que lo dejamos explícito.
    return `Falta configurar tu ANTHROPIC_API_KEY. Configúrala en /ajustes/anthropic.${
      err.hint ? ` ${err.hint}` : ''
    }`;
  }
  if (err instanceof CoachUpstreamError) {
    return 'Error de Anthropic, vuelve a intentar en un momento.';
  }
  if (err instanceof CoachInvalidPlanError) {
    return `El coach generó un plan que no respeta las reglas. Detalle: ${
      err.detail || 'sin detalle disponible'
    }`;
  }
  if (err instanceof CoachBadRequestError) {
    if (err.issues.length > 0) {
      return `Petición inválida: ${err.issues.join(' · ')}`;
    }
    return 'Petición inválida. Revisa los campos del formulario.';
  }
  return `No se pudo generar el plan. ${err.message ?? ''}`.trim();
}

// ---------------------------------------------------------------------------
// Spinner (inline, sin lib)
// ---------------------------------------------------------------------------

function Spinner(): ReactElement {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}

// ---------------------------------------------------------------------------
// M2.46.B — Presets de énfasis de la semana
// ---------------------------------------------------------------------------
//
// Cada preset traduce a una instrucción en castellano que se prepende al
// campo `constraints` que ya acepta el backend (max 1000 chars). Ningún
// preset supera 200 chars para dejar holgura al usuario.

export type EmphasisPreset =
  | 'balanced'
  | 'more_bike'
  | 'more_run'
  | 'more_swim'
  | 'more_strength'
  | 'more_volume'
  | 'less_volume';

export const EMPHASIS_PRESETS: ReadonlyArray<{
  value: EmphasisPreset;
  label: string;
}> = [
  { value: 'balanced', label: 'Equilibrado (default)' },
  { value: 'more_bike', label: '+ Bici (priorizar ciclismo)' },
  { value: 'more_run', label: '+ Carrera (priorizar run)' },
  { value: 'more_swim', label: '+ Nado (priorizar natación)' },
  { value: 'more_strength', label: '+ Fuerza (gym + core)' },
  { value: 'more_volume', label: '↑ Volumen (semana de carga alta)' },
  { value: 'less_volume', label: '↓ Volumen (semana ligera / descarga)' },
];

/**
 * Texto en castellano que se prepende al constraints. El LLM lo ve antes de
 * cualquier nota del usuario, así que cuenta como instrucción fuerte.
 */
export function emphasisInstruction(preset: EmphasisPreset): string {
  switch (preset) {
    case 'balanced':
      return '';
    case 'more_bike':
      return 'PRIORIDAD ESTA SEMANA: aumentar el volumen y/o intensidad de BICI (~+20-30% sobre lo habitual), manteniendo nado y carrera al mínimo de sostén.';
    case 'more_run':
      return 'PRIORIDAD ESTA SEMANA: aumentar el volumen y/o intensidad de CARRERA A PIE (~+20-30%), manteniendo bici y nado al mínimo de sostén.';
    case 'more_swim':
      return 'PRIORIDAD ESTA SEMANA: aumentar volumen de NATACIÓN (~+30%) — más sesiones cortas o una sesión técnica extra. Resto de disciplinas mantienen tendencia.';
    case 'more_strength':
      return 'PRIORIDAD ESTA SEMANA: incluir 2-3 sesiones explícitas de FUERZA / GIMNASIO (45-60 min cada una) además del triatlón. Reducir un 10-15% el volumen de las disciplinas para dejar hueco.';
    case 'more_volume':
      return 'SEMANA DE CARGA: aumentar el volumen total un ~15-20% respecto a las horas semanales del plan, manteniendo la distribución por disciplinas. Cuidado con la calidad: si hay fatiga acumulada, prioriza Z2 sobre Z4.';
    case 'less_volume':
      return 'SEMANA DE DESCARGA: reducir el volumen total un ~25-30% respecto a las horas semanales del plan. Mantén la frecuencia (cada disciplina al menos 1 vez) pero acorta las sesiones largas. Buena oportunidad para técnica y movilidad.';
  }
}

// ---------------------------------------------------------------------------
// Helpers (puros — fáciles de testear si hace falta)
// ---------------------------------------------------------------------------

/**
 * Devuelve `YYYY-MM-DD` del lunes de la próxima semana en hora local.
 * Si hoy es lunes, devuelve el lunes de DENTRO de 7 días.
 */
/** Convierte un ISO 8601 a etiqueta corta es-ES "1 jun" para el banner. */
function shortDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      day: 'numeric',
      month: 'short',
    }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}

function nextMondayISODate(): string {
  const now = new Date();
  // getDay(): 0=domingo, 1=lunes, …, 6=sábado
  const day = now.getDay();
  const daysUntilNextMon = ((1 - day + 7) % 7) || 7;
  const next = new Date(now);
  next.setDate(now.getDate() + daysUntilNextMon);
  const yyyy = next.getFullYear();
  const mm = String(next.getMonth() + 1).padStart(2, '0');
  const dd = String(next.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function clampHours(raw: string): number {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return 0;
  if (n > 24) return 24;
  return n;
}

function sumHours(h: HoursPerDay): number {
  return DAY_KEYS.reduce((acc, k) => acc + (Number(h[k]) || 0), 0);
}

function formatHours(h: number): string {
  if (Number.isInteger(h)) return `${h} h`;
  return `${h.toFixed(1)} h`;
}

function dayLabel(d: DayKey): string {
  switch (d) {
    case 'mon': return 'Lun';
    case 'tue': return 'Mar';
    case 'wed': return 'Mié';
    case 'thu': return 'Jue';
    case 'fri': return 'Vie';
    case 'sat': return 'Sáb';
    case 'sun': return 'Dom';
  }
}

export function goalLabel(g: Goal): string {
  switch (g) {
    case 'recovery': return 'Recuperación';
    case 'base': return 'Base aeróbica';
    case 'build': return 'Construcción';
    case 'peak': return 'Pico';
    case 'taper': return 'Puesta a punto';
    case 'test': return 'Test/Control';
    case 'race_week': return 'Semana de competición';
  }
}
